import { MessageService } from 'primeng/api'
import { Component } from '@angular/core'

import { AmortizationSummary, summarizeAmortization } from '@data/utils/amortization-table.util'
import { PdfBillingStatementService } from '@data/services/utils/pdf-billing-statement.service'
import { LeaseDetailService } from '../../data/services/view/lease-detail.service'

@Component({
  selector: 'app-lease-billing-resume',
  templateUrl: './lease-billing-resume.component.html',
  styleUrls: ['./lease-billing-resume.component.scss'],
  providers: [MessageService],
})
export class LeaseBillingResumeComponent {
  // NOTA: la gestión de comentarios se movió a la pestaña Bitácora (componente lease-activity).
  // Los comentarios no pertenecen a Cobranza. Ver `api-rendafin/.claude/audit-trail.md`.

  constructor(
    public leaseDetailService: LeaseDetailService,
    private messageService: MessageService,
    private pdfBillingService: PdfBillingStatementService,
  ) {}

  get data() {
    return this.leaseDetailService.data as any
  }

  /** Lentes de cartera (F36): la renta siempre cuenta; accesorios, residual y mora se encienden y apagan. */
  lens = { accessories: true, residual: true, mora: true }

  get components(): { rent: number; accessories: number; mora: number; residual: number } | null {
    return this.data?.wallet?.components ?? null
  }

  //* Cualquier combinación se arma sumando componentes aquí: el backend no necesita una consulta por lente.
  get carteraFiltrada(): number {
    const c = this.components
    if (!c) return 0
    return c.rent + (this.lens.accessories ? c.accessories : 0) + (this.lens.residual ? c.residual : 0) + (this.lens.mora ? c.mora : 0)
  }

  //* B-085: renta, inicio y cobradas/anticipadas salen de la tabla de amortización, no de `termData`. Se
  //* memoiza contra las tres referencias de las que depende porque la plantilla la lee muchas veces por
  //* ciclo, y `termData` puede llegar después que el arrendamiento (se calcula aparte).
  private tableSources: unknown[] = []
  private tableCache: AmortizationSummary | null = null
  get table(): AmortizationSummary {
    const sources = [this.data?.quotation?.amortizationPeriods, this.leaseDetailService.getTermDataSafely(), this.data?.rents]
    if (!this.tableCache || sources.some((s, i) => s !== this.tableSources[i])) {
      this.tableSources = sources
      this.tableCache = summarizeAmortization(sources[0] as any[], sources[1], sources[2])
    }
    return this.tableCache
  }

  /**
   * Descargar Estado de Cuenta en PDF
   */
  async downloadAccountStatement(): Promise<void> {
    try {
      // Validar que existan los datos necesarios
      if (!this.leaseDetailService.getTermDataSafely()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'No hay término de cotización aprobado para generar el estado de cuenta.',
        })
        return
      }

      // Mostrar loading
      this.messageService.add({
        severity: 'info',
        summary: 'Generando PDF',
        detail: 'Generando el estado de cuenta...',
        life: 2000,
      })

      // Generar PDF
      await this.pdfBillingService.generateBillingStatement(this.data, this.leaseDetailService.getTermDataSafely(), this.table)

      // Éxito
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'El estado de cuenta ha sido descargado correctamente.',
      })
    } catch (error) {
      console.error('Error al generar estado de cuenta:', error)
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al generar el estado de cuenta. Por favor intente nuevamente.',
      })
    }
  }
}
